import { useMsal } from "@azure/msal-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  listCoordinatorClients,
  updateCoordinatorClient,
  deleteCoordinatorClient,
  createCoordinatorClient,
  listAdminClients,
} from "../services/pdfService";
import { useI18n } from "../i18n";
import ClientThumb from "./ClientThumb";
import "./CoordinatorPage.css";

const TRIGGER_OPTIONS = ["daily", "weekly", "monthly"];
const PERIOD_OPTIONS = ["last_day", "last_week", "last_month"];

function computeNextExecution(trigger) {
  const now = new Date();
  const next = new Date(now);
  next.setHours(8, 0, 0, 0);

  if (trigger === "daily") {
    if (now >= next) next.setDate(next.getDate() + 1);
    return next;
  }
  if (trigger === "weekly") {
    const dayOfWeek = next.getDay();
    const daysUntilMonday = dayOfWeek === 0 ? 1 : dayOfWeek === 1 && now < next ? 0 : 8 - dayOfWeek;
    next.setDate(next.getDate() + daysUntilMonday);
    return next;
  }
  if (trigger === "monthly") {
    if (now.getDate() === 1 && now < next) return next;
    next.setMonth(next.getMonth() + 1);
    next.setDate(1);
    return next;
  }
  return null;
}

function formatNextExecution(date) {
  if (!date) return "-";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year} 08:00`;
}

function daysUntil(date) {
  if (!date) return Infinity;
  return Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

const EMPTY_SCHEDULE_FORM = {
  client_name: "",
  report_name: "",
  email: "",
  trigger: "daily",
  period: "last_day",
  active: true,
};

// ── Main CoordinatorPage ─────────────────────────────────────────────

const CoordinatorPage = () => {
  const { instance, accounts } = useMsal();
  const { t } = useI18n();
  const [schedules, setSchedules] = useState([]);
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [triggerFilter, setTriggerFilter] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_SCHEDULE_FORM);
  const [editingId, setEditingId] = useState(null);
  const [formError, setFormError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedIds, setExpandedIds] = useState({});

  const getApiToken = useCallback(async () => {
    const result = await instance.acquireTokenSilent({
      scopes: ["api://c247d402-5b9d-40f2-b7e6-b15a47c56f22/Api.access"],
      account: accounts[0],
    });
    return result.accessToken;
  }, [accounts, instance]);

  const loadData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const token = await getApiToken();
      const [schedData, clientData] = await Promise.all([
        listCoordinatorClients({ token }),
        listAdminClients({ token }),
      ]);
      setSchedules(Array.isArray(schedData.clients) ? schedData.clients : []);
      setClients(Array.isArray(clientData.clients) ? clientData.clients : []);
    } catch (e) {
      setError(e.message || t("coord_load_error"));
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [getApiToken, t]);

  useEffect(() => { loadData(); }, [loadData]);

  const clientMap = useMemo(() => {
    const map = {};
    clients.forEach((c) => { map[c.id] = c; });
    return map;
  }, [clients]);

  const uniqueClientNames = useMemo(() => {
    const set = new Set();
    clients.forEach((c) => { if (c.client_name) set.add(c.client_name); });
    return Array.from(set).sort();
  }, [clients]);

  const templatesForSelectedClient = useMemo(() => {
    if (!formData.client_name) return [];
    return clients
      .filter((c) => c.client_name === formData.client_name)
      .map((c) => c.template_name || "");
  }, [clients, formData.client_name]);

  const filteredSchedules = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return schedules.filter((s) => {
      const matchesTrigger = triggerFilter === "all" || s.trigger === triggerFilter;
      const matchesSearch = !search
        || (s.client_name || "").toLowerCase().includes(search)
        || (s.report_name || "").toLowerCase().includes(search)
        || (s.dashboard_url || "").toLowerCase().includes(search)
        || (s.email || []).some((e) => e.toLowerCase().includes(search));
      return matchesTrigger && matchesSearch;
    });
  }, [schedules, triggerFilter, searchText]);

  const handleToggleActive = useCallback(async (sched) => {
    if (togglingId) return;
    setTogglingId(sched.id);
    try {
      const token = await getApiToken();
      await updateCoordinatorClient(sched.id, { active: !sched.active }, { token });
      await loadData({ silent: true });
    } catch (e) {
      setError(e.message);
    } finally {
      setTogglingId(null);
    }
  }, [getApiToken, loadData, togglingId]);

  const handleDelete = useCallback(async (sched) => {
    if (deletingId) return;
    setDeletingId(sched.id);
    try {
      const token = await getApiToken();
      await deleteCoordinatorClient(sched.id, { token });
      await loadData({ silent: true });
    } catch (e) {
      setError(e.message);
    } finally {
      setDeletingId(null);
    }
  }, [getApiToken, loadData, deletingId]);

  const openCreateForm = () => {
    setEditingId(null);
    setFormData(EMPTY_SCHEDULE_FORM);
    setFormError(null);
    setShowForm(true);
  };

  const openEditForm = (sched) => {
    setEditingId(sched.id);
    const fallbackClientName = sched.client_name || clientMap[sched.client_id]?.client_name || "";
    setFormData({
      client_name: fallbackClientName,
      report_name: sched.report_name || "",
      email: (sched.email || []).join(", "),
      trigger: sched.trigger,
      period: sched.period,
      active: sched.active,
    });
    setFormError(null);
    setShowForm(true);
  };

  const handleFormSubmit = useCallback(async (e) => {
    e.preventDefault();
    setFormError(null);
    setIsSaving(true);

    const emailList = formData.email.split(",").map((s) => s.trim()).filter(Boolean);
    if (!formData.client_name) { setFormError(t("clients_err_name")); setIsSaving(false); return; }
    if (!formData.report_name) { setFormError(t("coord_err_report_name")); setIsSaving(false); return; }
    if (emailList.length === 0) { setFormError(t("coord_err_email")); setIsSaving(false); return; }

    const matchingClient = clients.find(
      (c) => c.client_name === formData.client_name && (c.template_name || "") === formData.report_name
    );
    if (!matchingClient) {
      setFormError(t("coord_err_client_template_match"));
      setIsSaving(false);
      return;
    }

    const payload = {
      client_id: Number(matchingClient.id),
      report_name: formData.report_name,
      dashboard_url: matchingClient.grafana_url,
      email: emailList,
      trigger: formData.trigger,
      period: formData.period,
      active: formData.active,
    };

    try {
      const token = await getApiToken();
      if (editingId) {
        await updateCoordinatorClient(editingId, payload, { token });
      } else {
        await createCoordinatorClient(payload, { token });
      }
      setShowForm(false);
      setFormData(EMPTY_SCHEDULE_FORM);
      setEditingId(null);
      await loadData({ silent: true });
    } catch (err) {
      setFormError(err.message);
    } finally {
      setIsSaving(false);
    }
  }, [editingId, formData, clients, getApiToken, loadData, t]);

  const activeCount = schedules.filter((s) => s.active).length;
  const dailyCount = schedules.filter((s) => s.active && s.trigger === "daily").length;
  const weeklyCount = schedules.filter((s) => s.active && s.trigger === "weekly").length;
  const monthlyCount = schedules.filter((s) => s.active && s.trigger === "monthly").length;

  return (
    <div className="coord-page">
      <div className="coord-section">
        <div className="coord-header">
          <div className="coord-stats">
            <div className="coord-stat">
              <span className="coord-stat-value">{activeCount}</span>
              <span className="coord-stat-label">{t("coord_active")}</span>
            </div>
            <div className="coord-stat coord-stat-daily">
              <span className="coord-stat-value">{dailyCount}</span>
              <span className="coord-stat-label">{t("coord_daily")}</span>
            </div>
            <div className="coord-stat coord-stat-weekly">
              <span className="coord-stat-value">{weeklyCount}</span>
              <span className="coord-stat-label">{t("coord_weekly")}</span>
            </div>
            <div className="coord-stat coord-stat-monthly">
              <span className="coord-stat-value">{monthlyCount}</span>
              <span className="coord-stat-label">{t("coord_monthly")}</span>
            </div>
          </div>
          <div className="coord-header-actions">
            <button className="button-generate coord-add-btn" onClick={openCreateForm}>+ {t("coord_add_client")}</button>
            <button className="button-generate" onClick={() => loadData()} disabled={isLoading}>
              {isLoading ? (
                <span className="btn-loading-content"><span className="button-spinner" aria-hidden="true" />{t("loading")}</span>
              ) : t("refresh")}
            </button>
          </div>
        </div>

        {error && <p className="coord-error">{error}</p>}

        <div className="coord-filters">
          <input
            className="coord-search"
            type="text"
            placeholder={t("coord_search_new")}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <select value={triggerFilter} onChange={(e) => setTriggerFilter(e.target.value)}>
            <option value="all">{t("coord_all_triggers")}</option>
            {TRIGGER_OPTIONS.map((v) => (<option key={v} value={v}>{v}</option>))}
          </select>
        </div>

        {showForm && (
          <div className="coord-form-overlay" onClick={() => setShowForm(false)}>
            <form className="coord-form" onClick={(e) => e.stopPropagation()} onSubmit={handleFormSubmit}>
              <h3>{editingId ? t("coord_edit_client") : t("coord_add_client")}</h3>
              {formError && <p className="coord-form-error">{formError}</p>}

              <label>{t("client")}</label>
              <select
                value={formData.client_name}
                onChange={(e) => setFormData((p) => ({ ...p, client_name: e.target.value, report_name: "" }))}
              >
                <option value="">{t("select_client")}</option>
                {uniqueClientNames.map((name) => (<option key={name} value={name}>{name}</option>))}
              </select>

              <label>{t("coord_report_name")}</label>
              <select
                value={formData.report_name}
                onChange={(e) => setFormData((p) => ({ ...p, report_name: e.target.value }))}
                disabled={!formData.client_name}
              >
                <option value="">
                  {formData.client_name ? t("coord_select_report_name") : t("select_client_first")}
                </option>
                {templatesForSelectedClient.map((name) => (
                  <option key={name || "__default__"} value={name}>
                    {name || "(default)"}
                  </option>
                ))}
              </select>
              {formData.client_name && templatesForSelectedClient.length === 0 && (
                <p className="coord-form-hint">{t("coord_no_template_for_client")}</p>
              )}

              <label>Emails ({t("coord_email_hint")})</label>
              <textarea rows={3} value={formData.email} onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))} />

              <label>Trigger</label>
              <select value={formData.trigger} onChange={(e) => setFormData((p) => ({ ...p, trigger: e.target.value }))}>
                {TRIGGER_OPTIONS.map((v) => (<option key={v} value={v}>{v}</option>))}
              </select>

              <label>{t("period")}</label>
              <select value={formData.period} onChange={(e) => setFormData((p) => ({ ...p, period: e.target.value }))}>
                {PERIOD_OPTIONS.map((v) => (<option key={v} value={v}>{v}</option>))}
              </select>

              <label className="coord-checkbox-label">
                <input type="checkbox" checked={formData.active} onChange={(e) => setFormData((p) => ({ ...p, active: e.target.checked }))} />
                {t("coord_active")}
              </label>

              <div className="coord-form-actions">
                <button type="button" className="coord-cancel-btn" onClick={() => setShowForm(false)}>{t("cancel")}</button>
                <button type="submit" className="button-generate" disabled={isSaving}>
                  {isSaving ? (
                    <span className="btn-loading-content"><span className="button-spinner" aria-hidden="true" />{t("loading")}</span>
                  ) : editingId ? t("coord_save") : t("coord_create")}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="coord-table-wrap">
          <table className="coord-table">
            <thead>
              <tr>
                <th>{t("coord_status_col")}</th>
                <th>{t("client")}</th>
                <th>{t("coord_report_name")}</th>
                <th>Trigger</th>
                <th>{t("coord_next_exec")}</th>
                <th>Emails</th>
                <th className="coord-th-expand" />
                <th>{t("action")}</th>
              </tr>
            </thead>
            <tbody>
              {!isLoading && filteredSchedules.length === 0 && (
                <tr><td colSpan={8} className="coord-empty">{t("coord_no_client")}</td></tr>
              )}
              {filteredSchedules.map((sched) => {
                const nextDate = computeNextExecution(sched.trigger);
                const days = daysUntil(nextDate);
                const urgencyClass = sched.active
                  ? days <= 1 ? "coord-next-imminent" : days <= 7 ? "coord-next-soon" : ""
                  : "coord-next-inactive";
                const isExpanded = !!expandedIds[sched.id];

                return (
                  <React.Fragment key={sched.id}>
                    <tr
                      className={`coord-row-clickable ${!sched.active ? "coord-row-inactive" : ""} ${isExpanded ? "coord-row-expanded" : ""}`}
                      onClick={() => setExpandedIds((prev) => ({ ...prev, [sched.id]: !prev[sched.id] }))}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <button
                          className={`coord-toggle ${sched.active ? "coord-toggle-on" : "coord-toggle-off"}`}
                          onClick={() => handleToggleActive(sched)}
                          disabled={togglingId === sched.id}
                          title={sched.active ? t("coord_deactivate") : t("coord_activate")}
                        >
                          {sched.active ? t("coord_on") : t("coord_off")}
                        </button>
                      </td>
                      <td className="coord-client-name">
                        <div className="coord-client-cell">
                          <ClientThumb clientName={sched.client_name || clientMap[sched.client_id]?.client_name} getApiToken={getApiToken} size={20} />
                          {sched.client_name || clientMap[sched.client_id]?.client_name || <span className="coord-client-missing">Client #{sched.client_id} manquant</span>}
                        </div>
                      </td>
                      <td className="coord-template">{sched.report_name}</td>
                      <td>
                        <span className={`coord-trigger-badge coord-trigger-${sched.trigger}`}>{sched.trigger}</span>
                      </td>
                      <td className={`coord-next ${urgencyClass}`}>
                        {sched.active ? formatNextExecution(nextDate) : "-"}
                      </td>
                      <td className="coord-emails">{(sched.email || []).length} {t("coord_recipients")}</td>
                      <td className="coord-expand-cell">
                        <span className={`coord-expand-icon ${isExpanded ? "coord-expand-icon-open" : ""}`}>&#9662;</span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="coord-action-group">
                          <button className="coord-edit-btn" onClick={() => openEditForm(sched)}>{t("coord_edit")}</button>
                          <button className="coord-delete-btn" onClick={() => handleDelete(sched)} disabled={deletingId === sched.id}>
                            {deletingId === sched.id ? t("deleting") : t("delete")}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="coord-details-row">
                        <td colSpan={8}>
                          <div className="coord-details-grid">
                            <div>
                              <strong>{t("coord_dashboard_url")} :</strong>{" "}
                              <a href={sched.dashboard_url} target="_blank" rel="noopener noreferrer" className="coord-detail-mono">{sched.dashboard_url}</a>
                            </div>
                            <div><strong>{t("period")} :</strong> {sched.period}</div>
                          </div>
                          <div className="coord-detail-emails">
                            <strong>{t("email_recipients")} :</strong>
                            <ul>{(sched.email || []).map((email, idx) => (<li key={idx}>{email}</li>))}</ul>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CoordinatorPage;
